import { useSearchParams } from "react-router-dom";
import type {
  BaseListViewDef,
  ListSortState,
  SortDir,
} from "@/base/list/types";
import type { FilterOptions } from "@/base/list/useFilterOptions";

// A list's view lives in the URL (BRK-S211), so a link, a reload and the
// browser's Back all show the same list. It is never kept in browser storage
// (CLAUDE.md #9).
//
// - A search writes with `replace`: refining a search isn't a step to go back
//   through. A page change writes with `push`, so Back returns to the page
//   before.
// - Any change other than the page drops back to page 1.
// - Defaults are left out, so the plain URL is the default view.
// - A filter value the definition doesn't offer is dropped, never sent: a
//   hand-edited URL mustn't put a value PMS would reject into a request. While
//   a filter's options are still being read from PMS (BRK-S254), its value
//   waits (`waiting`) instead: it can't be checked yet, and dropping it would
//   show the whole list under a URL that names one product.
// - A link param is removed by a real link ("Show all", `searchWithout`), so
//   Back returns to the narrowed list the link opened.
// - Every key is the definition's own, the page's too (`pageKey`, BRK-S406),
//   so two embedded lists on one page keep two views.

const PAGE_KEY = "page";

type Controls = Pick<
  BaseListViewDef<unknown>,
  "search" | "toggles" | "filters" | "linkParams" | "sort" | "pageKey"
>;

export interface ListUrlState {
  search: string;
  /** Each declared toggle, by `apiParam`. */
  toggles: Record<string, boolean>;
  /** Each declared filter, by `apiParam`; undefined is "all". */
  filters: Record<string, string | undefined>;
  /** Each declared link param, by `apiParam`; undefined when unset. */
  links: Record<string, string | undefined>;
  sort?: ListSortState;
  /** 1-based. */
  page: number;
  /** A filter value in the URL awaits its options, so the list can't load. */
  waiting: boolean;
  setSearch: (value: string) => void;
  setToggle: (apiParam: string, on: boolean) => void;
  setFilter: (apiParam: string, value: string | undefined) => void;
  /** Search, toggles and filters back to their defaults; links stay. */
  clearFilters: () => void;
  /** The query string with that link param gone, for a real "Show all" link. */
  searchWithout: (apiParam: string) => string;
  setSort: (sort: ListSortState) => void;
  setPage: (page: number) => void;
}

export function useListUrlState(
  def: Controls,
  /** Each filter's options by `apiParam` (useFilterOptions). */
  offered: Record<string, FilterOptions>,
): ListUrlState {
  const [params, setParams] = useSearchParams();
  const pageKey = def.pageKey ?? PAGE_KEY;

  const search = def.search ? (params.get(def.search.urlKey) ?? "") : "";
  const toggles = Object.fromEntries(
    (def.toggles ?? []).map((t) => [t.apiParam, params.get(t.urlKey) === "1"]),
  );
  let waiting = false;
  const filters = Object.fromEntries(
    (def.filters ?? []).map((f) => {
      const raw = params.get(f.urlKey);
      const options = offered[f.apiParam] ?? "loading";
      if (options === "loading" && raw) waiting = true;
      return [
        f.apiParam,
        typeof options === "string"
          ? undefined
          : options.find((o) => o.value === raw)?.value,
      ];
    }),
  );
  const links = Object.fromEntries(
    (def.linkParams ?? []).map((l) => [
      l.apiParam,
      params.get(l.urlKey)?.trim() || undefined,
    ]),
  );
  const sort = def.sort ? readSort(def.sort, params) : undefined;
  const page = Math.max(1, Math.floor(Number(params.get(pageKey))) || 1);

  function write(
    changes: Record<string, string | null>,
    history: "push" | "replace",
  ) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    if (!(pageKey in changes)) next.delete(pageKey);
    setParams(next, { replace: history === "replace" });
  }

  return {
    search,
    toggles,
    filters,
    links,
    sort,
    page,
    waiting,
    setSearch: (value) => {
      if (!def.search) return;
      write({ [def.search.urlKey]: value.trim() || null }, "replace");
    },
    setToggle: (apiParam, on) => {
      const toggle = def.toggles?.find((t) => t.apiParam === apiParam);
      if (!toggle) return;
      write({ [toggle.urlKey]: on ? "1" : null }, "replace");
    },
    setFilter: (apiParam, value) => {
      const filter = def.filters?.find((f) => f.apiParam === apiParam);
      if (!filter) return;
      write({ [filter.urlKey]: value || null }, "replace");
    },
    clearFilters: () =>
      write(
        Object.fromEntries(
          [
            ...(def.search ? [def.search.urlKey] : []),
            ...(def.toggles ?? []).map((t) => t.urlKey),
            ...(def.filters ?? []).map((f) => f.urlKey),
          ].map((key) => [key, null]),
        ),
        "replace",
      ),
    searchWithout: (apiParam) => {
      const link = def.linkParams?.find((l) => l.apiParam === apiParam);
      const next = new URLSearchParams(params);
      if (link) next.delete(link.urlKey);
      next.delete(pageKey);
      const search = next.toString();
      return search ? `?${search}` : "";
    },
    setSort: (next) => {
      if (!def.sort) return;
      const { field, dir, default: fallback } = def.sort;
      write(
        {
          [field.urlKey]: next.field === fallback.field ? null : next.field,
          [dir.urlKey]: next.dir === fallback.dir ? null : next.dir,
        },
        "replace",
      );
    },
    setPage: (next) =>
      write({ [pageKey]: next <= 1 ? null : String(next) }, "push"),
  };
}

/** The URL's sort, falling back to the default for anything it doesn't know. */
function readSort(
  sort: NonNullable<Controls["sort"]>,
  params: URLSearchParams,
): ListSortState {
  const field = params.get(sort.field.urlKey);
  const dir = params.get(sort.dir.urlKey);
  const known = sort.options.find((o) => o.value === field);
  return {
    field: known ? known.value : sort.default.field,
    dir: isDir(dir) ? dir : sort.default.dir,
  };
}

function isDir(value: string | null): value is SortDir {
  return value === "asc" || value === "desc";
}
